import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Scale, Search, Plus, Edit, Trash2, CheckCircle, AlertTriangle, Calendar,
  ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter,
  Eye, X, XCircle, LayoutGrid, Layers, TrendingUp,
  List, LayoutList, Shield, FileText,
  Hash, Tag, Package, Ruler, Calculator, Link2
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arSA from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import { useTranslation } from 'react-i18next';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  buildArticleStatusBadgeOptions,
  buildArticleStatusColumnFilterOptions,
  buildArticleStatusSelectOptions,
} from './articleCatalogStatusOptions';
import { getArticleStatusLabel } from './articlesStatusUtils';
import {buildBulkDeleteFilterAction,
  createArticleStatusChangeHandler,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemColumnVisibility, QawaemTableStickyActionsTd, sliceArticlesListItems } from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import { dispatchCatalogUnitsRefresh, fetchAllCatalogUnitsWithUsage } from './catalogUnitOptions';
import { articleFormLabelClass, ERP_MODAL_FORM_CLASS } from '../facturation/components/articleFormFieldClasses';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

registerLocale('fr', fr);
registerLocale('ar-SA', arSA);

const API_BASE = '/api/articles/unites-mesure';
const UOM_NS = 'modules.article.pages.unitesMesurePage';

const UNIT_TYPE_DEFS = [
  { value: 'Poids', labelKey: 'unit_type_weight' },
  { value: 'Volume', labelKey: 'unit_type_volume' },
  { value: 'Longueur', labelKey: 'unit_type_length' },
  { value: 'Surface', labelKey: 'unit_type_surface' },
  { value: 'Quantité', labelKey: 'unit_type_quantity' },
  { value: 'Temps', labelKey: 'unit_type_time' },
  { value: 'Énergie', labelKey: 'unit_type_energy' },
];

const UNIT_SCOPE_DEFS = [
  { value: 'product', labelKey: 'unit_scope_product' },
  { value: 'service', labelKey: 'unit_scope_service' },
];

const UnitesMesurePage = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  const articlesConfig = useArticlesConfig();
  const p = (key, options) => t(`${UOM_NS}.${key}`, options);
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = isSaudiBrand() ? 'ar-SA' : 'fr';

  const unitTypeSuggestions = useMemo(
    () => UNIT_TYPE_DEFS.map(({ value, labelKey }) => ({
      value,
      label: p(labelKey),
    })),
    [t],
  );

  const statusSuggestions = useMemo(() => buildArticleStatusSelectOptions(t), [t]);

  const unitTypeLabelMap = useMemo(() => {
    const entries = UNIT_TYPE_DEFS.map(({ value, labelKey }) => [value, p(labelKey)]);
    const map = Object.fromEntries(entries);
    for (const [value, label] of entries) {
      map[value.toLowerCase()] = label;
    }
    return map;
  }, [t]);

  const unitScopeSuggestions = useMemo(
    () => UNIT_SCOPE_DEFS.map(({ value, labelKey }) => ({
      value,
      label: p(labelKey),
    })),
    [t],
  );

  const unitScopeLabelMap = useMemo(
    () => Object.fromEntries(UNIT_SCOPE_DEFS.map(({ value, labelKey }) => [value, p(labelKey)])),
    [t],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [units, setUnits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  const [formTab, setFormTab] = useState('identity');

  const [formData, setFormData] = useState({
    unit_number: '',
    name: '',
    symbol: '',
    unit_type: '',
    unit_scope: 'product',
    base_unit_id: '',
    conversion_factor: '',
    decimal_places: '',
    status: 'actif',
    notes: '',
  });

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_units');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'وحدة القياس' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'وحدة قياس' });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);

  useEffect(() => {
    loadUnits();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnits = async () => {
    try {
      const { units: rows } = await fetchAllCatalogUnitsWithUsage();
      setUnits(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading units of measure:', error);
      setUnits([]);
    }
  };

  const distinctTypes = useMemo(() => {
    return new Set(units.map((u) => u.unit_type).filter(Boolean)).size;
  }, [units]);

  const getUsageCount = useCallback((unit) => {
    const product = parseInt(unit?.usage_product_count, 10) || 0;
    const service = parseInt(unit?.usage_service_count, 10) || 0;
    const total = product + service;
    if (total > 0) return total;
    return parseInt(unit?.usage_count ?? unit?.product_count, 10) || 0;
  }, []);

  const formatUsageLabel = useCallback(
    (record) => {
      const productCount = parseInt(record?.usage_product_count, 10) || 0;
      const serviceCount = parseInt(record?.usage_service_count, 10) || 0;
      if (productCount === 0 && serviceCount === 0) return p('usage_none');
      const parts = [];
      if (productCount > 0) parts.push(p('usage_products', { count: productCount }));
      if (serviceCount > 0) parts.push(p('usage_services', { count: serviceCount }));
      return parts.join(' · ');
    },
    [p],
  );

  const usageCellClass = useCallback(
    (record) => {
      const used = getUsageCount(record) > 0;
      if (isDashboardLightMode) {
        return used
          ? 'font-semibold text-sm text-[#2d6a2d]'
          : 'font-semibold text-sm text-[#8a7355]';
      }
      return used
        ? 'font-semibold text-sm text-emerald-300'
        : 'font-semibold text-sm text-white/70';
    },
    [getUsageCount, isDashboardLightMode],
  );

  const stats = {
    total: units.length,
    actives: units.filter((u) => u.status === 'actif').length,
    inactives: units.filter((u) => u.status === 'inactif').length,
    distinctTypes,
    unitsInUse: units.filter((u) => getUsageCount(u) > 0).length,
    linkedProducts: units.reduce((sum, u) => sum + getUsageCount(u), 0),
    unitsWithProducts: units.filter((u) => getUsageCount(u) > 0).length,
  };

  const mainTabs = useMemo(
    () => [
      { id: 'all_units', name: t('common.general_view'), icon: LayoutGrid },
      { id: 'par_statut', name: t('common.by_status'), icon: Shield },
      { id: 'par_type', name: t('common.by_type'), icon: Tag },
      { id: 'par_produits', name: p('tab_by_products'), icon: Package },
    ],
    [t],
  );

  const quickFilterButtons = useMemo(
    () => [
      { id: 'all', name: t('common.all'), icon: Layers },
      { id: 'actif', name: p('quick_active'), icon: CheckCircle },
      { id: 'inactif', name: p('quick_inactive'), icon: XCircle },
      { id: 'with_products', name: p('quick_with_products'), icon: Link2 },
    ],
    [t],
  );

  const getTypeDistribution = () => {
    const typeMap = {};
    units.forEach((u) => {
      const key = u.unit_type || '__undefined__';
      typeMap[key] = (typeMap[key] || 0) + 1;
    });
    const colors = [
      'bg-gradient-to-r from-blue-500 to-blue-600',
      'bg-gradient-to-r from-green-500 to-green-600',
      'bg-gradient-to-r from-purple-500 to-purple-600',
      'bg-gradient-to-r from-orange-500 to-orange-600',
      'bg-gradient-to-r from-yellow-500 to-yellow-600',
      'bg-gradient-to-r from-pink-500 to-pink-600',
    ];
    return Object.entries(typeMap).map(([key, count], i) => ({
      filterKey: key,
      name: key === '__undefined__' ? p('dist_undefined_type') : (unitTypeLabelMap[key] || key),
      count,
      color: colors[i % colors.length],
    }));
  };

  const getProductDistribution = () => {
    const withProducts = units.filter((u) => getUsageCount(u) > 0).length;
    const withoutProducts = units.length - withProducts;
    const highUsage = units.filter((u) => getUsageCount(u) >= 10).length;
    const mediumUsage = units.filter((u) => {
      const c = getUsageCount(u);
      return c >= 1 && c < 10;
    }).length;
    return [
      { filterKey: 'with_products', name: p('dist_with_products'), count: withProducts, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      { filterKey: 'without_products', name: p('dist_without_products'), count: withoutProducts, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
      { filterKey: 'high_usage', name: p('dist_high_usage'), count: highUsage, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      { filterKey: 'low_usage', name: p('dist_low_usage'), count: mediumUsage, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    ];
  };

  const unitDistribution = useMemo(() => ({
    all_units: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.actives, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'inactive', name: p('dist_inactive'), count: stats.inactives, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'distinct_types', name: p('dist_distinct_types'), count: stats.distinctTypes, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'linked_products', name: p('dist_linked_products'), count: stats.unitsWithProducts, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.actives, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'inactive', name: p('dist_inactive'), count: stats.inactives, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: getTypeDistribution(),
    },
    par_produits: {
      title: p('dist_by_products_title'),
      data: getProductDistribution(),
    },
  }), [units, stats, t]);

  const distributionInfo = unitDistribution[mainViewMode] || unitDistribution.all_units;

  const baseUnitOptions = useMemo(() => {
    return units
      .filter((u) => !editingRecord || u.id !== editingRecord.id)
      .map((u) => ({
        value: u.id,
        label: `${u.unit_number || ''} — ${u.name}${u.symbol ? ` (${u.symbol})` : ''}`,
      }));
  }, [units, editingRecord]);

  const baseFilteredUnits = useMemo(
    () =>
      units.filter((u) => {
        if (statusFilter !== 'all' && u.status !== statusFilter) return false;

        if (dateRange.start || dateRange.end) {
          const createdDate = u.created_at ? new Date(u.created_at) : null;
          if (createdDate) {
            if (dateRange.start) {
              const startOfDay = new Date(dateRange.start);
              startOfDay.setHours(0, 0, 0, 0);
              if (createdDate < startOfDay) return false;
            }
            if (dateRange.end) {
              const endOfDay = new Date(dateRange.end);
              endOfDay.setHours(23, 59, 59, 999);
              if (createdDate > endOfDay) return false;
            }
          }
        }

        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          const haystack = [u.unit_number, u.name, u.symbol, u.unit_type, u.notes]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(term)) return false;
        }

        if (mainViewMode === 'all_units') {
          if (quickFilter === 'actif' && u.status !== 'actif') return false;
          if (quickFilter === 'inactif' && u.status !== 'inactif') return false;
          if (quickFilter === 'with_products' && getUsageCount(u) === 0) return false;
        }

        if (activeDistributionFilter) {
          switch (mainViewMode) {
            case 'all_units':
            case 'par_statut':
              if (activeDistributionFilter === 'active' && u.status !== 'actif') return false;
              if (activeDistributionFilter === 'inactive' && u.status !== 'inactif') return false;
              if (activeDistributionFilter === 'linked_products' && getUsageCount(u) === 0) return false;
              break;
            case 'par_type':
              if (activeDistributionFilter !== (u.unit_type || '__undefined__')) return false;
              break;
            case 'par_produits':
              if (activeDistributionFilter === 'with_products' && getUsageCount(u) === 0) return false;
              if (activeDistributionFilter === 'without_products' && getUsageCount(u) > 0) return false;
              if (activeDistributionFilter === 'high_usage' && getUsageCount(u) < 10) return false;
              if (activeDistributionFilter === 'low_usage') {
                const c = getUsageCount(u);
                if (c < 1 || c >= 10) return false;
              }
              break;
            default:
              break;
          }
        }

        return true;
      }),
    [units, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, getUsageCount],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('unit_number', (r) => r.unit_number || ''),
      textColumnRule('created_at', (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString(dateLocale) : '',
      ),
      textColumnRule('name', (r) => r.name || ''),
      textColumnRule('symbol', (r) => r.symbol || ''),
      textColumnRule('unit_type', (r) => r.unit_type || ''),
      textColumnRule('conversion_factor', (r) => String(parseFloat(r.conversion_factor) || 0)),
      textColumnRule('product_count', (r) => String(getUsageCount(r))),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [dateLocale, getUsageCount],
  );

  const filteredUnits = useMemo(
    () => applyColumnFilters(baseFilteredUnits, colFilters, columnFilterRules),
    [baseFilteredUnits, colFilters, columnFilterRules],
  );

  const filteredUnitIds = useMemo(() => filteredUnits.map((u) => u.id), [filteredUnits]);
  const displayedUnits = useMemo(
    () => sliceArticlesListItems(filteredUnits, articlesConfig.itemsPerPage),
    [filteredUnits, articlesConfig.itemsPerPage],
  );
  const allRowsSelected = filteredUnitIds.length > 0 && filteredUnitIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const generateUnitNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'unitOfMeasure', 'UOM');
    return generateEntityReference(prefix, units, (u) => u.unit_number);
  }, [units, settings]);

  const resetForm = () => {
    setFormData({
      unit_number: '',
      name: '',
      symbol: '',
      unit_type: '',
      unit_scope: 'product',
      base_unit_id: '',
      conversion_factor: '',
      decimal_places: '',
      status: 'actif',
      notes: '',
    });
    setEditingRecord(null);
    setShowModal(false);
    setFormTab('identity');
  };

  const openNewUnitModal = () => {
    setFormData({
      unit_number: articlesConfig.autoGenerateUomReference !== false ? generateUnitNumber() : '',
      name: '',
      symbol: '',
      unit_type: '',
      unit_scope: 'product',
      base_unit_id: '',
      conversion_factor: '',
      decimal_places: '',
      status: 'actif',
      notes: '',
    });
    setEditingRecord(null);
    setFormTab('identity');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      window.alert(p('name_required'));
      return;
    }
    try {
      const unitNumber = (formData.unit_number || '').trim()
        || (articlesConfig.autoGenerateUomReference !== false ? generateUnitNumber() : '');
      const payload = {
        unit_number: unitNumber,
        name: formData.name.trim(),
        symbol: formData.symbol || null,
        unit_type: formData.unit_type || null,
        unit_scope: formData.unit_scope || 'product',
        base_unit_id: formData.base_unit_id || null,
        conversion_factor: formData.conversion_factor !== '' ? parseFloat(formData.conversion_factor) : 1,
        decimal_places: formData.decimal_places !== '' ? parseInt(formData.decimal_places, 10) : 2,
        status: formData.status || 'actif',
        notes: formData.notes || null,
      };

      if (editingRecord) {
        await localApiAdapter.put(`${API_BASE}/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post(API_BASE, payload);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      loadUnits();
      dispatchCatalogUnitsRefresh();
    } catch (error) {
      console.error('Error saving unit:', error);
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      unit_number: record.unit_number || '',
      name: record.name || '',
      symbol: record.symbol || '',
      unit_type: record.unit_type || '',
      unit_scope: record.unit_scope || 'product',
      base_unit_id: record.base_unit_id || '',
      conversion_factor: record.conversion_factor != null ? String(record.conversion_factor) : '',
      decimal_places: record.decimal_places != null ? String(record.decimal_places) : '',
      status: record.status || '',
      notes: record.notes || '',
    });
    setFormTab('identity');
    setShowModal(true);
  };

  const uomInputClass =
    'w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all';

  const formTabs = useMemo(() => ([
    { id: 'identity', label: p('section_identity'), icon: Hash, iconClassName: 'text-emerald-400' },
    { id: 'conversion', label: p('section_conversion'), icon: Ruler, iconClassName: 'text-blue-400' },
  ]), [p]);

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        await localApiAdapter.delete(`${API_BASE}/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        loadUnits();
        dispatchCatalogUnitsRefresh();
      } else {
        await Promise.all(ids.map((id) => localApiAdapter.delete(`${API_BASE}/${id}`)));
        notifyBulkDeleted(showNotify, ids.length, entityLabelPlural);
        setSelectedRecords([]);
        loadUnits();
        dispatchCatalogUnitsRefresh();
      }
    } catch (error) {
      console.error('Error deleting unit:', error);
      notifyError(showNotify, ids.length > 1 ? p('bulk_delete_error') : p('delete_error'));
    }
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredUnitIds);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords(
      selectedRecords.includes(id)
        ? selectedRecords.filter((rId) => rId !== id)
        : [...selectedRecords, id],
    );
  };

  const getExportData = () =>
    selectedRecords.length > 0
      ? units.filter((u) => selectedRecords.includes(u.id))
      : filteredUnits;

  const handleExportCSV = () => {
    exportToCSV(getExportData(), `unites_mesure_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), `unites_mesure_${new Date().toISOString().split('T')[0]}.xlsx`);
    setShowExportDropdown(false);
  };

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(getExportData(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `unites_mesure_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleImportCSV = () => {
    triggerFileInput(async (file) => {
      try {
        const imported = await importFromFile(file);
        if (!Array.isArray(imported)) {
          alert(p('import_invalid_format'));
          return;
        }
        for (const item of imported) {
          await localApiAdapter.post(API_BASE, item);
        }
        showNotify(p('import_success', { count: imported.length }), 'success');
        loadUnits();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        showNotify(p('import_error'), 'error');
      }
    }, '.csv');
  };

  const handleImportExcel = () => {
    triggerFileInput(async (file) => {
      try {
        const imported = await importFromFile(file);
        const rows = Array.isArray(imported) ? imported : [imported];
        for (const item of rows) {
          await localApiAdapter.post(API_BASE, item);
        }
        showNotify(p('import_success', { count: rows.length }), 'success');
        loadUnits();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        showNotify(p('import_error'), 'error');
      }
    }, '.xlsx,.xls');
  };

  const handleImportJSON = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        const rows = Array.isArray(data) ? data : [data];
        for (const item of rows) {
          await localApiAdapter.post(API_BASE, item);
        }
        showNotify(p('import_success', { count: rows.length }), 'success');
        loadUnits();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        showNotify(p('import_json_error'), 'error');
      }
    }, '.json');
  };

  const qawaem = isSaudiBrand();

  const qawaemStatusOptions = useMemo(
    () => [
      { value: 'all', label: p('all_statuses') },
      ...buildArticleStatusSelectOptions(t).map((o) => ({ value: o.value, label: o.label })),
    ],
    [t, p],
  );

  const qawaemStatusLabel =
    qawaemStatusOptions.find((o) => o.value === statusFilter)?.label ?? p('all_statuses');

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'unit_number', 'created_at', 'name', 'symbol', 'unit_type', 'conversion_factor', 'product_count', 'status',
  ]);

  const qawaemUomColumns = useMemo(
    () => [
      { key: 'unit_number', label: p('col_unit_number'), checked: columnVisibility.unit_number !== false, required: true },
      { key: 'created_at', label: p('col_created_at'), checked: columnVisibility.created_at !== false },
      { key: 'name', label: p('col_name'), checked: columnVisibility.name !== false, required: true },
      { key: 'symbol', label: p('col_symbol'), checked: columnVisibility.symbol !== false },
      { key: 'unit_type', label: p('col_type'), checked: columnVisibility.unit_type !== false },
      { key: 'conversion_factor', label: p('col_factor'), checked: columnVisibility.conversion_factor !== false },
      { key: 'product_count', label: p('col_usage'), checked: columnVisibility.product_count !== false },
      { key: 'status', label: t('common.status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: t('common.actions'), checked: true, required: true },
    ],
    [t, p, columnVisibility],
  );
  const qawaemVisibleUomColumnCount = qawaemUomColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stats_total'), value: stats.total, icon: Scale, variant: 'copper' },
      { key: 'active', label: p('stats_active'), value: stats.actives, icon: CheckCircle, variant: 'forest' },
      { key: 'inactive', label: p('stats_inactive'), value: stats.inactives, icon: XCircle, variant: 'copper' },
      { key: 'types', label: p('stats_units_in_use'), value: stats.unitsInUse, icon: Link2, variant: 'forest' },
    ],
    [stats, p],
  );

  const advancedOptionsContent = (
    <div className="space-y-4 pt-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setMainViewMode(tab.id);
                setQuickFilter('all');
                setActiveDistributionFilter(null);
              }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {mainViewMode === 'all_units' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setQuickFilter(filter.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  quickFilter === filter.id
                    ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button
              onClick={() => setActiveDistributionFilter(null)}
              className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
            >
              <XCircle size={14} />
              {p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
          {distributionInfo.data.map((item) => (
            <button
              key={item.filterKey || item.name}
              onClick={() =>
                setActiveDistributionFilter(
                  activeDistributionFilter === item.filterKey ? null : item.filterKey,
                )
              }
              className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                activeDistributionFilter === item.filterKey
                  ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20'
                  : 'border-white/10 hover:border-white/20 hover:bg-white/10'
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <h4
                  className={`font-medium ${
                    activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'
                  }`}
                >
                  {item.name}
                </h4>
                <div
                  className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}
                >
                  {item.count}
                </div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <div
                  className={`h-3 rounded-full ${item.color} transition-all`}
                  style={{
                    width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                    })()}%`,
                  }}
                />
              </div>
              <p
                className={`mt-2 text-sm ${
                  activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'
                }`}
              >
                {p('percent_of_total', {
                  percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                  })(),
                })}
              </p>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString(dateLocale);
    } catch {
      return dateStr;
    }
  };

  const getBaseUnitLabel = (baseUnitId) => {
    if (!baseUnitId) return '—';
    const base = units.find((u) => u.id === baseUnitId);
    return base ? `${base.name}${base.symbol ? ` (${base.symbol})` : ''}` : baseUnitId;
  };

  const STATUS_OPTIONS = useMemo(() => buildArticleStatusBadgeOptions(t), [t]);

  const statusColumnFilterOptions = useMemo(() => buildArticleStatusColumnFilterOptions(t), [t]);

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler(API_BASE, loadUnits),
    [],
  );

  const renderDatePickerHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
      <button
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        type="button"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '0.5rem',
          color: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
      </button>
      <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
        {date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
      </div>
      <button
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        type="button"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '0.5rem',
          color: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
      </button>
    </div>
  );

  return (
    <>
      <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />

      <FacturationListPageLayout
        className="space-y-6"
        containerClassName="employees-container"
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Scale}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildBulkDeleteFilterAction({
          count: selectedRecords.length,
          onClick: handleBulkDelete,
          label: p('delete_count', { count: selectedRecords.length }),
          title: p('delete_count', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder')}
        onCreate={openNewUnitModal}
        createLabel={p('new_unit')}
        importExport={{
          importOpen: showImportDropdown,
          exportOpen: showExportDropdown,
          onToggleImport: () => {
            setShowImportDropdown(!showImportDropdown);
            setShowExportDropdown(false);
          },
          onToggleExport: () => {
            setShowExportDropdown(!showExportDropdown);
            setShowImportDropdown(false);
          },
          importRef: importDropdownRef,
          exportRef: exportDropdownRef,
          importLabel: t('common.import'),
          exportLabel: t('common.export'),
          onImportCSV: handleImportCSV,
          onImportExcel: handleImportExcel,
          onImportJSON: handleImportJSON,
          onExportCSV: handleExportCSV,
          onExportExcel: handleExportExcel,
          onExportJSON: handleExportJSON,
        }}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {shouldShowCardsToolbarSearch(viewMode) ? (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : null}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards')}
                  tableTitle={t('common.view_table')}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('unit_number') ? <TableColumnHeaderFilter columnKey="unit_number" label={p('col_unit_number')} value={colFilters.unit_number ?? ''} onChange={(v) => setColFilter('unit_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('created_at') ? <TableColumnHeaderFilter columnKey="created_at" label={p('col_created_at')} value={colFilters.created_at ?? ''} onChange={(v) => setColFilter('created_at', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('name') ? <TableColumnHeaderFilter columnKey="name" label={p('col_name')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('symbol') ? <TableColumnHeaderFilter columnKey="symbol" label={p('col_symbol')} value={colFilters.symbol ?? ''} onChange={(v) => setColFilter('symbol', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('unit_type') ? <TableColumnHeaderFilter columnKey="unit_type" label={p('col_type')} value={colFilters.unit_type ?? ''} onChange={(v) => setColFilter('unit_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('conversion_factor') ? <TableColumnHeaderFilter columnKey="conversion_factor" label={p('col_factor')} value={colFilters.conversion_factor ?? ''} onChange={(v) => setColFilter('conversion_factor', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('product_count') ? <TableColumnHeaderFilter columnKey="product_count" label={p('col_usage')} value={colFilters.product_count ?? ''} onChange={(v) => setColFilter('product_count', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemUomColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleUomColumnCount}
                    totalCount={qawaemUomColumns.length}
                    thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedUnits.length > 0 ? (
                  displayedUnits.map((record, index) => (
                    <tr
                      key={record.id}
                      className={`hover:bg-white/5 transition-colors ${
                        selectedRecords.includes(record.id) ? 'bg-white/10' : ''
                      }`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {showCol('unit_number') ? (
                        <td className="px-6 py-4">
                          <span className="text-white font-semibold">{record.unit_number || '—'}</span>
                        </td>
                      ) : null}
                      {showCol('created_at') ? (
                        <td className="px-6 py-4">
                          <span className="text-white/70 text-sm">{formatDate(record.created_at)}</span>
                        </td>
                      ) : null}
                      {showCol('name') ? (
                        <td className="px-6 py-4">
                          <span className="text-white font-semibold">{record.name || '—'}</span>
                        </td>
                      ) : null}
                      {showCol('symbol') ? (
                        <td className="px-6 py-4">
                          <span className="text-white/70">{record.symbol || '—'}</span>
                        </td>
                      ) : null}
                      {showCol('unit_type') ? (
                        <td className="px-6 py-4">
                          <span className="text-white/70 text-sm">{unitTypeLabelMap[record.unit_type] || record.unit_type || '—'}</span>
                        </td>
                      ) : null}
                      {showCol('conversion_factor') ? (
                        <td className="px-6 py-4">
                          <span className="text-white font-bold">{record.conversion_factor ?? 1}</span>
                        </td>
                      ) : null}
                      {showCol('product_count') ? (
                        <td className="px-6 py-4">
                          <span className={usageCellClass(record)}>
                            {formatUsageLabel(record)}
                          </span>
                        </td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={STATUS_OPTIONS}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => {
                            setViewingRecord(record);
                            setShowViewModal(true);
                          }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Scale size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{p('footer_total', { count: filteredUnits.length })}</span>
                      <span>
                        {p('footer_active')}:{' '}
                        <span className="text-white font-semibold">
                          {filteredUnits.filter((u) => u.status === 'actif').length}
                        </span>
                      </span>
                      <span>
                        {p('footer_usage_total')}:{' '}
                        <span className="text-white font-semibold">
                          {filteredUnits.reduce((sum, u) => sum + getUsageCount(u), 0)}
                        </span>
                      </span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {displayedUnits.length > 0 ? (
              displayedUnits.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Scale size={20} className="text-emerald-400" />
                          {record.name || '—'}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={STATUS_OPTIONS}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                        {record.unit_number && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {record.unit_number}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_identity')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Hash size={16} className="text-blue-400" />
                              <span className="font-medium">{p('field_number_short')}:</span>{' '}
                              <span className="text-white">{record.unit_number || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Tag size={16} className="text-green-400" />
                              <span className="font-medium">{p('field_name')}:</span>{' '}
                              <span className="text-white">{record.name || '—'}</span>
                            </div>
                            <div>
                              <span className="font-medium">{p('field_symbol')}:</span> {record.symbol || '—'}
                            </div>
                            <div>
                              <span className="font-medium">{p('col_type')}:</span> {unitTypeLabelMap[record.unit_type] || record.unit_type || '—'}
                            </div>
                          </div>
                        </div>

                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_conversion')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Ruler size={16} className="text-purple-400" />
                              <span className="font-medium">{p('field_base_unit')}:</span>{' '}
                              <span className="text-white">{getBaseUnitLabel(record.base_unit_id)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calculator size={16} className="text-amber-400" />
                              <span className="font-medium">{p('field_factor_short')}:</span>{' '}
                              <span className="text-white">{record.conversion_factor ?? 1}</span>
                            </div>
                            {record.created_at && (
                              <div className="pt-2 border-t border-white/20">
                                <span className="font-medium">{t('common.created_at')}:</span>
                                <div className="text-white/90 text-xs mt-1">{formatDate(record.created_at)}</div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_parameters')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                              <span className="font-medium text-blue-300 text-xs">{p('field_decimals')}:</span>
                              <p className="text-white font-bold text-lg mt-1">{record.decimal_places ?? 2}</p>
                            </div>
                            <div className="p-2 bg-green-500/10 rounded border border-green-500/30">
                              <span className="font-medium text-green-300 text-xs">{p('field_usage_count')}:</span>
                              <p className={`font-bold text-sm mt-1 ${getUsageCount(record) > 0 ? 'text-emerald-700' : 'text-[#8a7355]'}`}>{formatUsageLabel(record)}</p>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.notes ? (
                              <div className="mt-2 p-2 bg-white/5 rounded">
                                <p className="text-white/60 text-xs">{record.notes}</p>
                              </div>
                            ) : (
                              <p className="text-white/40 text-xs italic">{p('field_no_notes')}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => {
                        setViewingRecord(record);
                        setShowViewModal(true);
                      }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Scale size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {showModal &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={resetForm}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              data-uom-modal-panel="true"
              className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${
                isDashboardLightMode
                  ? 'qawaem-modal-light border'
                  : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'
              }`}
              style={
                isDashboardLightMode
                  ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' }
                  : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }
              }
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
                <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
                  <Scale className={`h-6 w-6 ${isDashboardLightMode ? 'text-[#6e9b6e]' : 'text-blue-400'}`} />
                  {editingRecord ? p('modal_edit_title') : p('modal_new_title')}
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    form="uom-form"
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                  >
                    <CheckCircle size={18} />
                    {editingRecord ? t('common.save') : t('common.create')}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <QawaemErpModalFormTabBar
                tabs={formTabs}
                activeTab={formTab}
                onTabChange={setFormTab}
                isLight={isDashboardLightMode}
                ariaLabel={editingRecord ? p('modal_edit_title') : p('modal_new_title')}
              />

              <div className="overflow-y-auto p-6 flex-1 min-h-0">
                <form id="uom-form" className={ERP_MODAL_FORM_CLASS} onSubmit={handleSubmit}>
                  {formTab === 'identity' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="min-w-0">
                        <label className={articleFormLabelClass(isDashboardLightMode)}>{p('field_unit_number')}</label>
                        <input
                          type="text"
                          value={formData.unit_number}
                          onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                          className={`${uomInputClass} font-mono`}
                          placeholder={p('placeholder_unit_number', { defaultValue: 'UOM-001' })}
                          dir="ltr"
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={articleFormLabelClass(isDashboardLightMode)}>
                          <RequiredLabelText text={p('field_name_required')} required />
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className={uomInputClass}
                          placeholder={p('placeholder_name')}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={articleFormLabelClass(isDashboardLightMode)}>{p('field_symbol')}</label>
                        <input
                          type="text"
                          value={formData.symbol}
                          onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                          className={uomInputClass}
                          placeholder={p('placeholder_symbol')}
                          dir="ltr"
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={articleFormLabelClass(isDashboardLightMode)}>{p('field_unit_type')}</label>
                        <CustomSelect
                          value={formData.unit_type}
                          onChange={(val) => setFormData({ ...formData, unit_type: val })}
                          options={unitTypeSuggestions}
                          placeholder={p('placeholder_unit_type')}
                          searchable
                          creatable
                          autoSelect={false}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={articleFormLabelClass(isDashboardLightMode)}>{p('field_unit_scope')}</label>
                        <CustomSelect
                          value={formData.unit_scope || 'product'}
                          onChange={(val) => setFormData({ ...formData, unit_scope: val || 'product' })}
                          options={unitScopeSuggestions}
                          placeholder={p('placeholder_unit_scope')}
                          searchable={false}
                          creatable={false}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={articleFormLabelClass(isDashboardLightMode)}>{p('field_status')}</label>
                        <CustomSelect
                          value={formData.status}
                          onChange={(val) => setFormData({ ...formData, status: val })}
                          options={statusSuggestions}
                          placeholder={p('field_status_ph')}
                          searchable={false}
                          creatable={false}
                        />
                      </div>
                      <div className="min-w-0 md:col-span-3">
                        <label className={articleFormLabelClass(isDashboardLightMode)}>{p('field_notes')}</label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows={3}
                          className={uomInputClass}
                          placeholder={p('placeholder_notes')}
                        />
                      </div>
                    </div>
                  )}

                  {formTab === 'conversion' && (
                    <div className="space-y-4">
                      <p className={`text-sm ${isDashboardLightMode ? 'text-gray-600' : 'text-white/60'}`}>{p('conversion_hint')}</p>
                      <p className={`text-xs ${isDashboardLightMode ? 'text-[#704820]' : 'text-blue-300/80'}`}>{p('conversion_example_carton')}</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{p('field_base_unit')}</label>
                          <CustomSelect
                            value={formData.base_unit_id}
                            onChange={(val) => setFormData({ ...formData, base_unit_id: val })}
                            options={baseUnitOptions}
                            placeholder={p('placeholder_base_unit')}
                            searchable={true}
                            creatable={true}
                            autoSelect={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{p('field_conversion_factor')}</label>
                          <CustomNumberInput
                            step={0.0001}
                            value={formData.conversion_factor}
                            onChange={(v) => setFormData({ ...formData, conversion_factor: v })}
                            placeholder="1"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{p('field_decimals')}</label>
                          <CustomNumberInput
                            step={1}
                            value={formData.decimal_places}
                            onChange={(v) => setFormData({ ...formData, decimal_places: v })}
                            placeholder="2"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>,
          document.body,
        )}

      {showViewModal &&
        viewingRecord &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
              style={{
                boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
                width: '90%',
                maxWidth: '1000px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Scale className="h-6 w-6 text-blue-400" />
                  {p('view_details_title', { name: viewingRecord.name })}
                </h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>
              <div className="overflow-y-auto p-6 flex-1">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                    <h5 className="font-medium text-white mb-3 flex items-center gap-2">
                      <Hash size={18} className="text-emerald-400" />
                      {p('section_identity')}
                    </h5>
                    <div className="space-y-2 text-sm text-white/70">
                      <div><span className="font-medium">{p('field_unit_number')}:</span> {viewingRecord.unit_number || '—'}</div>
                      <div><span className="font-medium">{p('field_name')}:</span> {viewingRecord.name || '—'}</div>
                      <div><span className="font-medium">{p('field_symbol')}:</span> {viewingRecord.symbol || '—'}</div>
                      <div><span className="font-medium">{p('field_unit_scope')}:</span> {unitScopeLabelMap[viewingRecord.unit_scope || 'product'] || '—'}</div>
                      <div><span className="font-medium">{p('col_type')}:</span> {unitTypeLabelMap[viewingRecord.unit_type] || viewingRecord.unit_type || '—'}</div>
                      <div><span className="font-medium">{t('common.status')}:</span>{' '}
                        <QawaemClickableStatusBadge
                          status={viewingRecord.status}
                          recordId={viewingRecord.id}
                          options={STATUS_OPTIONS}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                    <h5 className="font-medium text-white mb-3 flex items-center gap-2">
                      <Ruler size={18} className="text-blue-400" />
                      {p('section_conversion')}
                    </h5>
                    <div className="space-y-2 text-sm text-white/70">
                      <div><span className="font-medium">{p('field_base_unit')}:</span> {getBaseUnitLabel(viewingRecord.base_unit_id)}</div>
                      <div><span className="font-medium">{p('field_factor_short')}:</span> {viewingRecord.conversion_factor ?? 1}</div>
                      <div><span className="font-medium">{t('common.created_at')}:</span> {formatDate(viewingRecord.created_at)}</div>
                      <div><span className="font-medium">{t('common.updated_at')}:</span> {formatDate(viewingRecord.updated_at)}</div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                    <h5 className="font-medium text-white mb-3 flex items-center gap-2">
                      <Calculator size={18} className="text-yellow-400" />
                      {p('section_parameters')}
                    </h5>
                    <div className="space-y-2 text-sm text-white/70">
                      <div><span className="font-medium">{p('field_decimals')}:</span> {viewingRecord.decimal_places ?? 2}</div>
                      <div><span className="font-medium">{p('field_usage_count')}:</span> {formatUsageLabel(viewingRecord)}</div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                    <h5 className="font-medium text-white mb-3 flex items-center gap-2">
                      <FileText size={18} className="text-red-400" />
                      {p('section_notes')}
                    </h5>
                    <p className="text-sm text-white/70">{viewingRecord.notes || p('field_no_notes')}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-white/10 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingRecord);
                  }}
                  className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-300 border border-green-500/30 rounded-xl transition-all flex items-center gap-2"
                >
                  <Edit size={16} />
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all"
                >
                  {t('common.close')}
                </button>
              </div>
            </motion.div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default UnitesMesurePage;

